import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrentContextService } from '../../core/context/current-context.service';
import { MessageTemplate } from './notification.models';
import { NotificationService } from './notification.service';
import { normalizeNotificationCategory, normalizeNotificationChannel } from './notification.utils';

@Component({
  standalone: true,
  selector: 'ain-notifications-templates',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, AinFormFieldComponent, AinCanAccessDirective, FormsModule, TableModule, ButtonModule, SelectModule, DialogModule, InputTextModule],
  template: `
    <ain-page [eyebrow]="t('notifications.eyebrow')" [title]="t('notifications.templates.title')" [description]="t('notifications.templates.description')">
      <ain-info-hint page-title-addon [titleKey]="'notifications.templates.help.title'" [descriptionKey]="'notifications.templates.help.description'"
        [bulletKeys]="['notifications.templates.help.bullets.versions','notifications.templates.help.bullets.publish','notifications.templates.help.bullets.preview']" />
      <section class="card toolbar">
        <ain-form-field [label]="t('notifications.messages.filters.channel')">
          <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="channelFilter" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
        </ain-form-field>
        <button pButton type="button" [label]="t('catalog.action.refresh')" (click)="load()"></button>
        <button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton type="button" [label]="t('notifications.templates.actions.create')" (click)="createDialog.set(true)"></button>
      </section>
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else if (templates().length === 0) { <ain-state type="empty" [title]="t('notifications.templates.empty')" /> }
      @else {
        <section class="card">
          <p-table [value]="templates()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr><th>{{ t('notifications.templates.columns.key') }}</th><th>{{ t('notifications.templates.columns.name') }}</th><th>{{ t('notifications.templates.columns.channel') }}</th><th>{{ t('notifications.templates.columns.category') }}</th><th></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.templateKey }}</button></td>
                <td>{{ row.displayName }}</td>
                <td>{{ channelLabel(row.channel) }}</td>
                <td>{{ categoryLabel(row.category) }}</td>
                <td><button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton size="small" [label]="t('common.edit')" (click)="openDetail(row.id)"></button></td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
      <p-dialog [header]="t('notifications.templates.create.title')" [visible]="createDialog()" (visibleChange)="createDialog.set($event)" [modal]="true">
        <div class="create-grid">
          <ain-form-field [label]="t('notifications.templates.columns.key')"><input pInputText [(ngModel)]="createForm.templateKey" /></ain-form-field>
          <ain-form-field [label]="t('notifications.templates.columns.name')"><input pInputText [(ngModel)]="createForm.displayName" /></ain-form-field>
          <ain-form-field [label]="t('notifications.templates.columns.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="createForm.channel" appendTo="body" /></ain-form-field>
          <ain-form-field [label]="t('notifications.templates.columns.category')"><p-select [options]="categoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="createForm.category" appendTo="body" /></ain-form-field>
          <ain-form-field [label]="t('notifications.templates.fields.defaultLocale')"><p-select [options]="localeOptions" optionLabel="label" optionValue="value" [(ngModel)]="createForm.defaultLocale" appendTo="body" /></ain-form-field>
        </div>
        <ng-template pTemplate="footer"><button ainCanAccess [ainCanAccessPermission]="'notifications.templates.manage'" pButton type="button" [label]="t('common.create')" [loading]="creating()" (click)="createTemplate()"></button></ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:12px}.toolbar{display:flex;gap:8px;align-items:end;flex-wrap:wrap}.link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}.create-grid{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}`]
})
export class NotificationsTemplatesComponent implements OnInit {
  private readonly api = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly i18n = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  readonly loading = signal(false);
  readonly templates = signal<MessageTemplate[]>([]);
  readonly createDialog = signal(false);
  readonly creating = signal(false);
  channelFilter: string | null = null;
  createForm = { templateKey: '', displayName: '', channel: 'Email', category: 'Transactional', defaultLocale: 'en-US' };
  readonly channelOptions = [{ label: 'Email', value: 'Email' }, { label: 'SMS', value: 'Sms' }];
  readonly categoryOptions = [{ label: 'Transactional', value: 'Transactional' }, { label: 'Service', value: 'Service' }, { label: 'Marketing', value: 'Marketing' }];
  readonly localeOptions = [{ label: 'English (US)', value: 'en-US' }, { label: 'فارسی', value: 'fa-IR' }];

  ngOnInit(): void { this.load(); }
  t(key: string): string { return this.i18n.translate(key); }
  channelLabel(channel: unknown): string { return this.t(`notifications.enums.channel.${normalizeNotificationChannel(channel as never)}`); }
  categoryLabel(category: unknown): string { return this.t(`notifications.enums.category.${normalizeNotificationCategory(category as never) ?? 'Other'}`); }
  openDetail(id: string): void { void this.router.navigate(['/notifications/templates', id]); }
  createTemplate(): void {
    if (!this.canManageTemplates()) return;
    if (!this.createForm.templateKey.trim() || !this.createForm.displayName.trim()) return;
    this.creating.set(true);
    this.api.createTemplate(this.createForm).pipe(finalize(() => this.creating.set(false))).subscribe({
      next: template => void this.router.navigate(['/notifications/templates', template.id])
    });
  }
  load(): void {
    this.loading.set(true);
    this.api.listTemplates(this.channelFilter ?? undefined).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: items => this.templates.set(items)
    });
  }

  private canManageTemplates(): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === 'notifications.templates.manage');
  }
}
